import { useState, useEffect } from 'react';
import { useTheme, useMediaQuery, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { promoBannersApi, PromoBanner } from '../../api/promoBanners';

export default function PromoBannerSlider() {
  const { i18n } = useTranslation();
  const [banners, setBanners] = useState<PromoBanner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setIsLoading(true)
        const activeBanners = await promoBannersApi.getActiveBanners(i18n.language);
        setBanners(activeBanners);
      } catch (error) {
        console.error('Error fetching promo banners:', error);
      } finally {
        setIsLoading(false)
      }
    };

    fetchBanners();
  }, [i18n.language]);

  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  // Get language-specific image URL
  const getImageUrl = (banner: PromoBanner): string => {
    const currentLanguage = i18n.language;
    
    if (isMobile) {
      // Mobile image
      switch (currentLanguage) {
        case 'zh':
          return banner.mobileImageUrlZh || banner.mobileImageUrl;
        case 'cn':
          return banner.mobileImageUrlCn || banner.mobileImageUrl;
        default:
          return banner.mobileImageUrl;
      }
    } else {
      // Desktop image
      switch (currentLanguage) {
        case 'zh':
          return banner.desktopImageUrlZh || banner.desktopImageUrl;
        case 'cn':
          return banner.desktopImageUrlCn || banner.desktopImageUrl;
        default:
          return banner.desktopImageUrl;
      }
    }
  };

  // Get language-specific title
  const getBannerTitle = (banner: PromoBanner): string => {
    const currentLanguage = i18n.language;
    
    switch (currentLanguage) {
      case 'zh':
        return banner.titleZh || banner.title;
      case 'cn':
        return banner.titleCn || banner.title;
      default:
        return banner.title;
    }
  };

  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  if (isLoading) {
    console.log('Showing skeleton, isLoading:', isLoading);
    return (
      <div style={{ marginLeft: '15px', marginRight: '15px' }}>
        <Skeleton
          variant="rounded"
          width="90vw"
          height={'120px'}
          sx={{ 
            borderRadius: 2,
            bgcolor: '#f0f0f0',
            marginLeft: '15px',
            marginRight: '15px',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, #e0e0e0, transparent)',
            }
          }}
        />
        <div style={{ 
          textAlign: 'center', 
          color: '#666',
          fontSize: '0.875rem'
        }}>
        </div>
      </div>
    );
  }

  if (banners.length === 0) {
    return null; // Don't render anything if no banners
  }

  return (
    <div style={{ 
      position: 'relative', 
      overflow: 'hidden',
      borderRadius: 8,
      marginTop: isMobile ? '0px' : '20px',
    }}>
      {/* Banner Image */}
      <div style={{ position: 'relative' }}>
        <img
          src={getImageUrl(banners[currentIndex])}
          alt={getBannerTitle(banners[currentIndex])}
          style={{
            width: 'calc(100vw - 30px)',
            maxWidth: '1200px',
            height: 'auto',
            maxHeight: '100px',
            objectFit: 'cover',
            borderRadius: 8,
            display: 'block',
          }}
        />
      </div>
    </div>
  );
}
